/**
 * 라이선스 기능(feature) 카탈로그 + 레벨(등급) 카탈로그.
 *
 * ## 두 축의 역할 분담
 *
 * - **features[]** 가 강제(enforcement)의 단독 책임자다. 제품(aisight) 은 오직 이 목록만 보고
 *   기능을 연다. 레벨은 여기에 개입하지 않는다.
 * - **level** 은 「이 라이선스가 무슨 상품인가」를 말하는 **표시·발급 편의용 이름**이다.
 *   발급 시 레벨을 고르면 features 를 카탈로그에서 파생해 저장하고, 토큰에도 표시용 claim 으로 실어 보낸다.
 *
 * 이렇게 나눈 이유: 레벨을 강제 경로에 넣으면 레벨을 모르는 구버전 제품이 기능을 잃는다.
 * features 만 보게 두면 새 레벨을 아무리 추가해도 구버전이 안전하게 동작한다.
 *
 * ## `'*'` 와일드카드의 의미 (중요)
 *
 * `'*'` 는 **발급 시점의 전체**다. 2026-09 이전 발급분은 거의 전부 `['*']` 이고, 그 시점에
 * 존재하지 않던 유료 기능(`studio.dashboard`)까지 소급해 주지는 않는다. 판정은 제품 쪽
 * `license/entitlements.ts` 의 `WILDCARD_EXCLUDED` 가 한다. 신규 유료 기능을 여기에 추가할 때는
 * 반드시 제품 쪽 그 집합도 같이 갱신할 것 — 빠뜨리면 기존 `*` 고객 전원에게 공짜로 열린다.
 */
export const WILDCARD_FEATURE = '*';
export const KNOWN_FEATURES = [
    { key: 'base', label: '기본 제품 (채팅·위키·대시보드 등 전체 기본 기능)', defaultOn: true },
    { key: 'studio', label: 'Studio (스튜디오 — 슬라이드·웹 리포트)', defaultOn: false },
    { key: 'studio.dashboard', label: 'Studio 대시보드 (생성·편집)', defaultOn: false },
];
/**
 * 레벨(등급) 카탈로그 — 계단식. 위에서 아래로 갈수록 상위 등급이고 하위 기능을 전부 포함한다.
 *
 * 솔루션이 늘어나면 이 배열을 `PRODUCTS[productKey].levels` 로 감싸면 된다 —
 * 지금 제품 축을 만들지 않은 것은 솔루션이 aisight 하나뿐이기 때문이고, 모양은 그때를 대비해 둔다.
 */
export const LICENSE_LEVELS = [
    {
        key: 'aisightCore',
        label: 'aisight core',
        description: '채팅·위키 등 기본 제품 + 스튜디오 슬라이드·웹 리포트',
        features: ['base', 'studio'],
    },
    {
        key: 'aisightPro',
        label: 'aisight pro',
        description: 'aisight core + 스튜디오 대시보드 생성·편집',
        features: ['base', 'studio', 'studio.dashboard'],
    },
];
/** 신규 발급의 기본 레벨. 기존 발급분도 이 이름으로 관리한다. */
export const DEFAULT_LICENSE_LEVEL = 'aisightCore';
/** claim 이 없는 레거시 토큰을 제품이 이 이름으로 떨어뜨린다 (양쪽이 같은 값을 써야 한다). */
export const LEGACY_LICENSE_LEVEL = 'aisightCore';
export function findLicenseLevel(level) {
    if (!level)
        return null;
    return LICENSE_LEVELS.find((l) => l.key === level) ?? null;
}
/** 레벨 키 → 표시명. 모르는 키/없음이면 null (호출자가 「사용자 지정」 등으로 표기). */
export function levelLabelOf(level) {
    return findLicenseLevel(level)?.label ?? null;
}
/** 레벨 키 → features 목록. 모르는 키/없음이면 null (= 수동 features 경로). */
export function featuresOfLevel(level) {
    const found = findLicenseLevel(level);
    return found ? [...found.features] : null;
}
//# sourceMappingURL=features.js.map